/**
 * One shared pointer for the whole app. A single passive `pointermove` listener writes
 * into a mutable object; nobody re-renders.
 *
 * Read it inside `useFrame`, a GSAP ticker or rAF callbacks, **never during render**:
 *
 * ```ts
 * useFrame(() => {
 *   mesh.current.rotation.y = damp(mesh.current.rotation.y, pointer.nx * 0.3, 4, delta)
 * })
 * ```
 *
 * @module
 */
import { gsap } from './gsap'
import { damp } from './math'

/** Shape of {@link pointer}. */
export interface PointerState {
  /** Client X in CSS px. */
  x: number
  /** Client Y in CSS px (down is positive). */
  y: number
  /** Normalised X in −1..1 (left → right), like NDC. */
  nx: number
  /** Normalised Y in −1..1 (**up is positive**), like NDC. */
  ny: number
  /** Smoothed X velocity in CSS px per second. It decays to 0 when the pointer rests. */
  vx: number
  /** Smoothed Y velocity in CSS px per second (down is positive). */
  vy: number
  /** `true` if the last pointer event came from touch or a pen. */
  isTouch: boolean
  /** `performance.now()` of the last move, or 0 before any movement. */
  lastMove: number
}

const hasWindow = typeof window !== 'undefined'

/** The shared, mutable pointer state. Starts centred, at rest. */
export const pointer: PointerState = {
  x: hasWindow ? window.innerWidth / 2 : 0,
  y: hasWindow ? window.innerHeight / 2 : 0,
  nx: 0,
  ny: 0,
  vx: 0,
  vy: 0,
  isTouch: hasWindow ? window.matchMedia('(pointer: coarse)').matches : false,
  lastMove: 0,
}

/** How quickly the smoothed velocity follows the raw velocity (see `damp`). */
const VELOCITY_LAMBDA = 12

let previousX = pointer.x
let previousY = pointer.y

function onPointerMove(event: PointerEvent): void {
  pointer.x = event.clientX
  pointer.y = event.clientY
  pointer.nx = (event.clientX / window.innerWidth) * 2 - 1
  pointer.ny = -((event.clientY / window.innerHeight) * 2 - 1)
  pointer.isTouch = event.pointerType !== 'mouse'
  pointer.lastMove = performance.now()
}

/** Runs once per frame on the GSAP ticker (the same ticker that drives Lenis). */
function updateVelocity(_time: number, deltaMs: number): void {
  const dt = Math.max(deltaMs, 1) / 1000
  const rawVx = (pointer.x - previousX) / dt
  const rawVy = (pointer.y - previousY) / dt
  previousX = pointer.x
  previousY = pointer.y
  pointer.vx = damp(pointer.vx, rawVx, VELOCITY_LAMBDA, dt)
  pointer.vy = damp(pointer.vy, rawVy, VELOCITY_LAMBDA, dt)
  if (Math.abs(pointer.vx) < 0.01) pointer.vx = 0
  if (Math.abs(pointer.vy) < 0.01) pointer.vy = 0
}

if (hasWindow) {
  window.addEventListener('pointermove', onPointerMove, { passive: true })
  gsap.ticker.add(updateVelocity)
  import.meta.hot?.dispose(() => {
    window.removeEventListener('pointermove', onPointerMove)
    gsap.ticker.remove(updateVelocity)
  })
}
